import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const Tarjetas = () => {
    const [tarjetas, setTarjetas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalCrear, setModalCrear] = useState(false);
    const [modalAsignar, setModalAsignar] = useState(null);
    const [nuevoCodigo, setNuevoCodigo] = useState('');
    const [nuevasNotas, setNuevasNotas] = useState('');
    const [userSeleccionado, setUserSeleccionado] = useState('');
    const [saving, setSaving] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const navigate = useNavigate();

    const cargar = () => {
        setLoading(true);
        Promise.all([
            api.get('/tarjetas'),
            api.get('/users')
        ]).then(([t, u]) => {
            setTarjetas(t.data);
            setUsuarios(u.data);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { cargar(); }, []);

    const estadoBadge = (estado) => {
        const map = {
            activa:      { label: 'Activa',      cls: 'bg-green-100 text-green-700' },
            sin_activar: { label: 'Sin activar', cls: 'bg-yellow-100 text-yellow-700' },
            bloqueada:   { label: 'Bloqueada',   cls: 'bg-red-100 text-red-700' },
        };
        const e = map[estado] || { label: estado, cls: 'bg-gray-100 text-gray-600' };
        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.cls}`}>{e.label}</span>;
    };

    const handleCrear = async () => {
        if (!nuevoCodigo.trim()) return;
        setSaving(true);
        try {
            await api.post('/tarjetas', { codigo: nuevoCodigo.trim(), notas: nuevasNotas });
            setMensaje('Tarjeta creada correctamente');
            setModalCrear(false);
            setNuevoCodigo('');
            setNuevasNotas('');
            cargar();
        } catch (e) {
            setMensaje(e.response?.data?.error || 'Error creando tarjeta');
        } finally {
            setSaving(false);
        }
    };

    const handleEstado = async (uuid, estado) => {
        try {
            await api.put(`/tarjetas/${uuid}/estado`, { estado });
            cargar();
        } catch (e) {
            setMensaje(e.response?.data?.error || 'Error actualizando estado');
        }
    };

    const handleAsignar = async () => {
        setSaving(true);
        try {
            await api.put(`/tarjetas/${modalAsignar}/asignar`, { user_uuid: userSeleccionado });
            setMensaje('Tarjeta asignada correctamente');
            setModalAsignar(null);
            setUserSeleccionado('');
            cargar();
        } catch (e) {
            setMensaje(e.response?.data?.error || 'Error asignando tarjeta');
        } finally {
            setSaving(false);
        }
    };

    const handleEliminar = async (uuid) => {
        if (!window.confirm('¿Eliminar esta tarjeta y su perfil? Esta acción no se puede deshacer.')) return;
        try {
            await api.delete(`/tarjetas/${uuid}`);
            cargar();
        } catch (e) {
            setMensaje(e.response?.data?.error || 'Error eliminando tarjeta');
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-64">
            <div className="text-gray-400 text-sm">Cargando tarjetas...</div>
        </div>
    );

    return (
        <div>
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Tarjetas NFC</h1>
                    <p className="text-gray-500 text-sm mt-1">{tarjetas.length} tarjeta{tarjetas.length !== 1 ? 's' : ''} registrada{tarjetas.length !== 1 ? 's' : ''}</p>
                </div>
                <button
                    onClick={() => setModalCrear(true)}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                    style={{ backgroundColor: 'var(--color-primary)' }}
                >
                    + Nueva tarjeta
                </button>
            </div>

            {mensaje && (
                <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm flex items-center justify-between">
                    {mensaje}
                    <button onClick={() => setMensaje(null)} className="text-blue-400 hover:text-blue-600">✕</button>
                </div>
            )}

            {/* Tabla */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Código</th>
                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Estado</th>
                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Usuario</th>
                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Activación</th>
                            <th className="text-left px-5 py-3 text-gray-500 font-medium">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {tarjetas.map(t => (
                            <tr key={t.uuid} className="hover:bg-gray-50 transition">
                                <td className="px-5 py-3 font-mono text-gray-700">{t.codigo}</td>
                                <td className="px-5 py-3">{estadoBadge(t.estado)}</td>
                                <td className="px-5 py-3 text-gray-600">
                                    {t.nombre ? `${t.nombre} ${t.apellido}` : <span className="text-gray-300">Sin asignar</span>}
                                </td>
                                <td className="px-5 py-3 text-gray-400 text-xs">
                                    {t.fecha_activacion
                                        ? new Date(t.fecha_activacion).toLocaleDateString('es-CO')
                                        : '—'}
                                </td>
                                <td className="px-5 py-3">
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => { setModalAsignar(t.uuid); setUserSeleccionado(t.user_uuid || ''); }}
                                            className="text-xs text-blue-500 hover:underline"
                                        >Asignar</button>
                                        <span className="text-gray-200">|</span>
                                        {t.estado !== 'activa' && (
                                            <button onClick={() => handleEstado(t.uuid, 'activa')}
                                                className="text-xs text-green-500 hover:underline">Activar</button>
                                        )}
                                        {t.estado === 'activa' && (
                                            <button onClick={() => handleEstado(t.uuid, 'bloqueada')}
                                                className="text-xs text-orange-500 hover:underline">Bloquear</button>
                                        )}
                                        {t.estado === 'bloqueada' && (
                                            <button onClick={() => handleEstado(t.uuid, 'activa')}
                                                className="text-xs text-green-500 hover:underline">Reactivar</button>
                                        )}
                                        <span className="text-gray-200">|</span>
                                        <button
                                            onClick={() => navigate(`/tarjetas/${t.uuid}/perfil`)}
                                            className="text-xs text-purple-500 hover:underline"
                                        >Perfil</button>
                                        <span className="text-gray-200">|</span>
                                        <button
                                            onClick={() => handleEliminar(t.uuid)}
                                            className="text-xs text-red-400 hover:underline"
                                        >Eliminar</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {tarjetas.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-5 py-10 text-center text-gray-300">No hay tarjetas registradas</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modal crear */}
            {modalCrear && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Nueva tarjeta NFC</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Código único *</label>
                                <input
                                    value={nuevoCodigo}
                                    onChange={e => setNuevoCodigo(e.target.value)}
                                    placeholder="Ej: NFC-0001"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Notas internas</label>
                                <input
                                    value={nuevasNotas}
                                    onChange={e => setNuevasNotas(e.target.value)}
                                    placeholder="Opcional"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-5">
                            <button onClick={() => setModalCrear(false)}
                                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button onClick={handleCrear} disabled={saving}
                                className="flex-1 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Creando...' : 'Crear'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal asignar */}
            {modalAsignar && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Asignar usuario</h3>
                        <select
                            value={userSeleccionado}
                            onChange={e => setUserSeleccionado(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        >
                            <option value="">— Sin asignar —</option>
                            {usuarios.map(u => (
                                <option key={u.uuid} value={u.uuid}>
                                    {u.nombre} {u.apellido} ({u.email})
                                </option>
                            ))}
                        </select>
                        <div className="flex gap-3 mt-5">
                            <button onClick={() => setModalAsignar(null)}
                                className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                                Cancelar
                            </button>
                            <button onClick={handleAsignar} disabled={saving}
                                className="flex-1 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Asignando...' : 'Asignar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tarjetas;
